import { Injectable } from '@angular/core'

import { AllocatableLease, DistributionResult } from '@modules/private/payment/components/lease-distribution-selector/lease-distribution-selector.component'
import { BatchLeaseOption, PaymentBatchNoticeResult, PaymentBatchParseResult, PaymentBatchRegisterResult } from '../api/api-payment-batch.service'

/** Qué hace el asistente con lo que se sube: solo darlo de alta, o darlo de alta y aplicarlo (D6). */
export type BatchMode = 'SOLO_ALTA' | 'ALTA_Y_APLICA'

/**
 * Los pasos, por CLAVE.
 *
 * **Nunca por índice.** El asistente tiene tres pasos o seis según el interruptor, y PrimeNG numera
 * solo lo que se renderiza: con `SOLO_ALTA` el índice 2 es «registro» y con `ALTA_Y_APLICA` el mismo
 * índice es otra cosa. Ese fallo ya ocurrió tres veces en el repo —el detalle del arrendamiento, el
 * listado de pagos y el contenedor de cobranza— y las tres en silencio.
 */
export type StepKey = 'archivo' | 'revision' | 'registro' | 'destinos' | 'impacto' | 'aplicacion'

/** Un pago ya registrado y lo que se ha decidido sobre él. */
export interface BatchPaymentRow {
	paymentId: string
	rowNumber: number
	clientId: string
	clientName: string
	amount: number
	/** Lo que le queda por aplicar. Con el lote recién registrado es el monto entero. */
	pendingToApply: number
	trackingKey: string
	/** ISO. La fecha en que el cliente pagó: es a la que corta el motor, no a hoy. */
	date: string | null
	/** Los arrendamientos vivos del cliente. Uno resuelve solo; varios exigen elegir (D3). */
	activeLeases: BatchLeaseOption[]
	/** Los mismos, con capacidad, cuando hubo que pedirlos para el selector. */
	allocatable: AllocatableLease[]
	/** Lo que el operador decidió repartir. Null mientras no haya varios destinos que decidir. */
	distribution: DistributionResult | null
	/** El impacto que devolvió la previsualización de ESTE pago. */
	preview: any | null
	previewError: string
	/** Las advertencias bloqueantes que el operador confirmó, por código. */
	confirmations: Record<string, boolean>
	/** El operador lo sacó del lote: se queda registrado y sin aplicar. */
	excluded: boolean
	applied: boolean
	applyError: string
}

/**
 * El estado del asistente de carga, en un solo sitio.
 *
 * Existe porque el asistente son seis pasos con dos puntos de no retorno, y encadenar eso con
 * `@Input`/`@Output` entre padre e hijos obliga a que el padre conozca el detalle de cada paso: deja
 * de ser un padre que delega y pasa a ser el paso más grande de todos. Aquí el padre solo sabe qué
 * paso está activo; cada paso lee y escribe lo suyo.
 *
 * No es un `providedIn: 'root'`: se provee en el componente del asistente, así que salir de la
 * pantalla lo destruye y volver a entrar empieza limpio. Un lote a medias que sobreviva a la
 * navegación es un lote que se aplica dos veces.
 */
@Injectable()
export class PaymentBatchStateService {
	/** El archivo que el operador eligió. Se conserva porque el alta lo vuelve a mandar. */
	file: File | null = null
	parse: PaymentBatchParseResult | null = null
	mode: BatchMode = 'ALTA_Y_APLICA'
	register: PaymentBatchRegisterResult | null = null
	notice: PaymentBatchNoticeResult | null = null
	/** Ya se le avisó a este lote en otra sesión: no se puede volver a avisar. */
	noticeAlreadySent: { sentAt: string; sentTo: number } | null = null
	rows: BatchPaymentRow[] = []
	uploadBatch = ''
	/** Si la mora está encendida. Decide el desglose del selector, no la capacidad. */
	moraEnabled = false

	activeKey: StepKey = 'archivo'

	/**
	 * Si el paso de Destinos hace falta en ESTE lote.
	 *
	 * Se decide **una sola vez, al registrar**, y ya no cambia. Recalcularlo en cada acceso sería lo
	 * natural y está mal: en cuanto el operador resolviera la última excepción el paso desaparecería
	 * bajo sus pies, con el asistente saltando solo a otra pantalla mientras aún está mirando lo que
	 * acaba de decidir.
	 *
	 * Antes del alta vale `false` y da igual: la tira lo pinta igual porque `registered` es falso —no
	 * se puede saber si hay excepciones hasta que los pagos existen y se sabe qué cliente es cada uno.
	 */
	needsDestinations = false

	//* Solo la etiqueta: la tira propia del asistente numera los pasos y marca el hecho con una palomita,
	//* así que el ícono por paso que pedía `p-steps` dejó de pintarse en ninguna parte.
	private readonly labels: Record<StepKey, string> = {
		archivo: 'Archivo',
		revision: 'Revisión',
		registro: 'Registro',
		destinos: 'Destinos',
		impacto: 'Impacto',
		aplicacion: 'Aplicación',
	}

	/**
	 * Los pasos que de verdad se pintan, EN ORDEN.
	 *
	 * Con «solo dar de alta» son tres y el asistente termina al registrar; con «dar de alta y aplicar»
	 * son seis. La lista sale siempre de aquí: es la que traduce una posición de PrimeNG a una clave.
	 */
	get visibleSteps(): { key: StepKey; label: string }[] {
		const claves: StepKey[] = this.mode === 'SOLO_ALTA' ? ['archivo', 'revision', 'registro'] : ['archivo', 'revision', 'registro', 'destinos', 'impacto', 'aplicacion']

		//* Destinos NO existe cuando no hay nada que decidir: un cliente con un solo arrendamiento
		//* activo lo resuelve el motor (D3). Hacer pasar por ahí un lote de cuarenta pagos donde los
		//* cuarenta se resuelven solos es el mismo tedio del que se queja Operaciones, reproducido
		//* dentro del asistente que venía a quitárselo.
		const finales = this.registered && !this.needsDestinations ? claves.filter((k) => k !== 'destinos') : claves

		return finales.map((key) => ({ key, label: this.labels[key] }))
	}

	/**
	 * Los pagos que EXIGEN una decisión: el cliente tiene varios arrendamientos activos, o ninguno.
	 *
	 * Es lo único que el paso de Destinos tiene que enseñar. Los demás ya tienen destino y listarlos
	 * solo obliga a recorrer con la vista treinta filas para encontrar las tres que importan.
	 */
	get exceptionRows(): BatchPaymentRow[] {
		return this.applicableRows.filter((r) => r.activeLeases.length !== 1)
	}

	/** Cuántos pagos resolvió el motor sin preguntar. Se dice, no se esconde. */
	get autoResolvedCount(): number {
		return this.applicableRows.filter((r) => r.activeLeases.length === 1).length
	}

	get activeIndex(): number {
		const i = this.visibleSteps.findIndex((s) => s.key === this.activeKey)
		return i >= 0 ? i : 0
	}

	/** Ya se registró: a partir de aquí el archivo no se puede cambiar ni el modo tampoco. */
	get registered(): boolean {
		return !!this.register || !!this.uploadBatch
	}

	/** Las filas que el archivo dejó listas para registrar. Las demás se reportan y no viajan. */
	get readyRows() {
		return (this.parse?.rows ?? []).filter((r) => r.status === 'LISTA')
	}

	/** Los pagos que van a recibir dinero: los registrados que el operador no sacó del lote. */
	get applicableRows(): BatchPaymentRow[] {
		return this.rows.filter((r) => !r.excluded && r.pendingToApply > 0.005)
	}

	/** Los que ya tienen su destino decidido: uno solo, o un reparto confirmado entre varios. */
	get decidedRows(): BatchPaymentRow[] {
		return this.applicableRows.filter((r) => this.isDecided(r))
	}

	/** Un pago con un solo arrendamiento activo no tiene nada que decidir (D3). */
	isDecided(row: BatchPaymentRow): boolean {
		if (row.activeLeases.length === 1) return true
		if (!row.activeLeases.length) return false

		return !!row.distribution && row.distribution.allocations.length > 0 && !row.distribution.needsConfirmation
	}

	/** Los pagos sin destino posible: el cliente no tiene ningún arrendamiento activo. */
	get orphanRows(): BatchPaymentRow[] {
		return this.rows.filter((r) => !r.excluded && !r.activeLeases.length)
	}

	get previewedRows(): BatchPaymentRow[] {
		return this.decidedRows.filter((r) => !!r.preview)
	}

	get appliedRows(): BatchPaymentRow[] {
		return this.rows.filter((r) => r.applied)
	}

	/** Los clientes distintos del lote: es a cuántas personas se les avisaría (D2). */
	get noticeClientCount(): number {
		return new Set(this.rows.map((r) => r.clientId)).size
	}

	/** Lo que el archivo dejó leído. Cambiar de archivo tira todo lo demás. */
	setParse(parse: PaymentBatchParseResult, file: File): void {
		this.file = file
		this.parse = parse
		this.uploadBatch = ''
		this.register = null
		this.rows = []
		this.notice = null
	}

	/**
	 * Lo que el alta creó. **Después de esto los pagos existen.**
	 *
	 * Cada pago se casa con su fila leída por el NÚMERO DE FILA y no por el orden de la lista: el alta
	 * omite las filas que fallaron, así que las dos listas no se corresponden posición a posición.
	 */
	setRegistered(result: PaymentBatchRegisterResult): void {
		this.register = result
		this.uploadBatch = result.uploadBatch

		const leidas = new Map((this.parse?.rows ?? []).map((r) => [r.rowNumber, r]))

		this.rows = (result.rows ?? [])
			.filter((r) => r.status === 'SUCCESS' && r.paymentId)
			.map((r) => {
				const leida = leidas.get(r.rowNumber)

				return {
					paymentId: r.paymentId as string,
					rowNumber: r.rowNumber,
					clientId: r.clientId || leida?.clientId || '',
					clientName: r.clientName || leida?.clientName || '—',
					amount: r.amount,
					pendingToApply: r.amount,
					trackingKey: r.trackingKey,
					date: leida?.date ?? null,
					activeLeases: leida?.activeLeases ?? [],
					allocatable: [],
					distribution: null,
					preview: null,
					previewError: '',
					confirmations: {},
					excluded: false,
					applied: false,
					applyError: '',
				}
			})

		this.latchDestinations()
	}

	/**
	 * Retoma un lote que ya existía, entrando por su ruta.
	 *
	 * Entre registrar y aplicar hay minutos de trabajo humano, y cerrar la pestaña en medio no puede
	 * dejar cuarenta pagos huérfanos. Lo que no se recupera son los destinos elegidos: se vuelven a
	 * decidir, que es lo correcto — el estado de los arrendamientos pudo cambiar entretanto.
	 */
	setRecovered(uploadBatch: string, payments: { paymentId: string; clientId: string; clientName: string; amount: number; pendingToApply: number; trackingKey: string; date: string }[], notice: { sentAt: string; sentTo: number } | null): void {
		this.uploadBatch = uploadBatch
		this.mode = 'ALTA_Y_APLICA'
		this.noticeAlreadySent = notice
		this.rows = payments.map((p, i) => ({
			paymentId: p.paymentId,
			rowNumber: i + 1,
			clientId: p.clientId,
			clientName: p.clientName,
			amount: p.amount,
			pendingToApply: p.pendingToApply,
			trackingKey: p.trackingKey,
			date: p.date,
			activeLeases: [],
			allocatable: [],
			distribution: null,
			preview: null,
			previewError: '',
			confirmations: {},
			excluded: false,
			applied: false,
			applyError: '',
		}))

		//! Un lote recuperado NO trae los arrendamientos de cada fila —los traía la lectura del
		//! archivo, que ya no está—, así que aquí todas las filas parecen huérfanas y el paso hace
		//! falta siempre. Es lo correcto: el paso 4 los pide y ahí se sabe cuáles se resuelven solos.
		this.latchDestinations()
	}

	/**
	 * Fija si Destinos hace falta, con las filas ya construidas. Se llama una vez por lote.
	 *
	 * Cuenta también a los huérfanos: un pago cuyo cliente no tiene ningún arrendamiento activo no
	 * tiene a dónde ir y **alguien tiene que sacarlo del lote a mano**, así que el paso hace falta.
	 */
	private latchDestinations(): void {
		this.needsDestinations = this.exceptionRows.length > 0
	}

	/**
	 * Cambiar un destino tira la previsualización de ese pago.
	 *
	 * La huella de estado se calculó sobre unos arrendamientos concretos: si cambian el orden, las
	 * casillas o el modo, el plan que está en pantalla ya no es el que se guardaría.
	 */
	setDistribution(row: BatchPaymentRow, result: DistributionResult): void {
		const cambio = !row.distribution || JSON.stringify(row.distribution.allocations) !== JSON.stringify(result.allocations) || row.distribution.distribution !== result.distribution
		row.distribution = result
		if (cambio) this.invalidatePreview(row)
	}

	invalidatePreview(row: BatchPaymentRow): void {
		row.preview = null
		row.previewError = ''
		row.confirmations = {}
	}

	/**
	 * El cuerpo con el que se previsualiza y se aplica ESTE pago.
	 *
	 * Es el mismo en los dos casos a propósito: lo que se aplica tiene que ser exactamente lo que se
	 * previsualizó, y la huella de estado que viaja al aplicar es la que devolvió esa previsualización.
	 * El aviso viaja apagado siempre — el lote avisa una vez al final, no una por pago (D2).
	 */
	buildPayload(row: BatchPaymentRow, conHuella: boolean): any {
		const base: any = { payment: row.paymentId, notify: false }

		if (row.distribution && row.distribution.allocations.length) {
			base.distribution = row.distribution.distribution
			//* Sin tope por arrendamiento: el pago entero es lo que se está aplicando, así que el motor
			//* reparte con `distribution` y puede cascadear más allá de lo que el panel estimó.
			base.leases = row.distribution.allocations.map((a) => ({ lease: a.leaseId }))
		} else {
			base.leases = [{ lease: row.activeLeases[0]?.leaseId }]
		}

		if (conHuella && row.preview) {
			base.stateHash = row.preview.stateHash
			base.confirmations = Object.keys(row.confirmations).filter((k) => row.confirmations[k])
		}

		return base
	}

	/** Las advertencias bloqueantes de un pago que todavía no se confirmaron. */
	pendingWarnings(row: BatchPaymentRow): any[] {
		return ((row.preview?.warnings ?? []) as any[]).filter((w) => w.blocking && !row.confirmations[w.code])
	}

	/** Un pago se puede aplicar cuando tiene impacto calculado y nada bloqueante sin confirmar. */
	canApply(row: BatchPaymentRow): boolean {
		return !!row.preview && !row.applied && !this.pendingWarnings(row).length
	}

	goTo(key: StepKey): void {
		if (this.visibleSteps.some((s) => s.key === key)) this.activeKey = key
	}

	/** El paso siguiente, por posición en la lista VISIBLE. */
	next(): void {
		const siguiente = this.visibleSteps[this.activeIndex + 1]
		if (siguiente) this.activeKey = siguiente.key
	}

	back(): void {
		const anterior = this.visibleSteps[this.activeIndex - 1]
		if (anterior) this.activeKey = anterior.key
	}
}
